import { Component, Inject, AfterViewInit, EventEmitter, Output, ViewChild, Input } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { UserService } from '../../../humain/user/services/user.service';
import { PlanPreventionService } from '../../services/plan-prevention.service';
import { JSR310ConversionService } from "../../../../shared/jsr310-localdates/services/jsr310-conversion.service";

import { catchError } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { applicationConstants } from '../../../../app.constants';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { PdpSearchCriteria } from '../../entities/plan-prevention-search-criteria';
import { EtatPdp, PlanPreventionEntity } from '../../entities/plan-prevention.entity';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';
import { PdpExportCriteria, PdpExportCriteriaOption } from '../../entities/plan-prevention-export-criteria';


@Component({
	selector: 'liste-plan-preventions',
	templateUrl: 'liste-plan-preventions.html',
	styleUrls: ['liste-plan-preventions.css']
})
export class ListePlanPreventionsComponent implements AfterViewInit {
	
	searchCriteria: PdpSearchCriteria = new PdpSearchCriteria();
	user!: UserTLR;

	constructor(
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService, 
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		@Inject(UserService) private userService: UserService) {
			
		this.refreshCommandsSubject.subscribe(
			// lors de la reception d'une demande de refresh
			() => {
				this.loadPdps();
				this.searchCriteria.typePdp = [0, 1];
				this.searchCriteria.etat = [0, 1, 2, 3, 4, 6, 7, 8];
				this.searchCriteria.validite = [0, 1];
			}
		);
		this.filterCommandsSubject.subscribe(
			// lors de la reception d'une demande de refresh
			(searchCriteria) => {
				this.searchCriteria = searchCriteria;
				if (this.user) {
					this.searchCriteria.currentUserId = this.user.id;
				}
				this.dataSource.filter = JSON.stringify(searchCriteria);
			}
		);
	}
	
	// Elements injectes necessaires a la gestion du tableau
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;
	
	@Output() pdpForEdit = new EventEmitter<PlanPreventionEntity>();
	
	isLoading: boolean = true;
	versionInWhichValideursCanSignPdps: number = applicationConstants.versionInWhichValideursCanSignPdps;
	
	displayedColumns: string[] = ['id', 'numeroPdp', 'parcs', 'ouvrages', 'typePdp', 'entreprises', 'description', 'etat', 'dateDebut', 'dateFin', 'validite', 'joursAvantExpiration', 'lien', 'actions'];
	
	data: PlanPreventionEntity[] = [];
	dataSource: MatTableDataSource<PlanPreventionEntity> = new MatTableDataSource(this.data);
	
	resumeDialogCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	dialogSignatairesPdpSpecificCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	dialogSignatairesPdpGeneralCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	manageIntervenantsCommands: Subject<number> = new Subject<number>();
	manageLinkCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	generationCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	refreshValidationCommands: Subject<PlanPreventionEntity> = new Subject<PlanPreventionEntity>();
	
	private refreshCommandsSubject = new SubjectRelay<void>();
	@Input() set refreshCommands(obs: Observable<void> | undefined) {
		this.refreshCommandsSubject.registerSource(obs);
	}

	private filterCommandsSubject = new SubjectRelay<PdpSearchCriteria>();
	@Input() set filterCommands(obs: Observable<PdpSearchCriteria> | undefined) {
		this.filterCommandsSubject.registerSource(obs);
	}
	
	filterPredicate(pdp: PlanPreventionEntity, searchString: string) {
		const searchCriteria = JSON.parse(searchString);
		const nowInMillis: number = new Date().getTime();
		
		let retainResult: boolean = true;
		if (searchCriteria['numeroPdp'] || searchCriteria['numeroPdp'] === '') {
			retainResult = pdp.numeroPdp.toLocaleLowerCase().includes(searchCriteria['numeroPdp'].toLocaleLowerCase());
		}
		if (searchCriteria['entreprise']) {
			if (pdp.idEntreprises != null) {
				const entrepriseIds = pdp.idEntreprises.split(',').map( $ =>  Number($) );
				retainResult = retainResult && entrepriseIds.indexOf(searchCriteria['entreprise']) > -1;
			} else {
				return false;
			}
		}
		if (searchCriteria['typePdp']) {
			retainResult = retainResult && searchCriteria['typePdp'].indexOf(pdp.typePdp) > -1;
		}
		if (searchCriteria['etat']) {
			if (searchCriteria['etat'].length === 6) {
				// tous les états sont cochés
				retainResult = retainResult && true;
			} else if (pdp.obsolete) {
				// c'est un pdp archivé
				if (searchCriteria['etat'].indexOf(5) >= 0) {
					// on a coché l'état ARCHIVE
					retainResult = retainResult && true;
				} else {
					retainResult = false;
				}
			} else {
				// Mapping des valeurs à considérer
				// 		JAVA										TS
				// A_FAIRE_SIGNER			// Code 0 		-> searchCriteria 1
				// SIGNE					// Code 1 		-> searchCriteria 4
				// SIGNE_PARTIELLEMENT		// Code 2 		-> searchCriteria 3
				// BROUILLON				// Code 3 		-> searchCriteria 0
				// ENVOYE_VIA_DOCUSIGN		// Code 4 		-> searchCriteria 2
				// ATTENTE_DE_RELECTURE		// Code 5 		-> searchCriteria 6
				// INVALIDE					// Code 6 		-> searchCriteria 7
				
				const correspondances: number[] = [1, 4, 3, 0, 2, 6, 7];
				retainResult = retainResult && searchCriteria['etat'].indexOf(correspondances[pdp.etat]) > -1;
			}
		}
		if (searchCriteria['validite']) {
			if (pdp.finValidite) {
				let dateFin = new Date(pdp.finValidite[0], pdp.finValidite[1] - 1, pdp.finValidite[2]);
				let expiredPdp: number = nowInMillis > dateFin.getTime() ? 1 : 0;
				retainResult = retainResult && searchCriteria['validite'].indexOf(expiredPdp) > -1;
			}
		}
		if (searchCriteria['parc']) {
			retainResult = retainResult && pdp.parcIds.indexOf(searchCriteria['parc']) > -1;
		}
		if (searchCriteria['groupement']) {
			retainResult = retainResult && pdp.groupementIds && pdp.groupementIds.indexOf(searchCriteria['groupement']) > -1;
		}
		if (searchCriteria['antenne']) {
			retainResult = retainResult && searchCriteria['antenne'] === pdp.antenneId;
		}
		if (searchCriteria['agence']) {
			retainResult = retainResult && searchCriteria['agence'] === pdp.agenceId;
		}
		if (searchCriteria['monPerimetreReferent']) {
			retainResult = retainResult && pdp.referentParcId === searchCriteria['currentUserId'];
		}
		return retainResult;
	}
	
	ngAfterViewInit() {
		this.userService.getCurrentUserTLR().subscribe(
			(user) => {
				this.user = user;
				// Initialisation du dataSource
				this.dataSource.paginator = this.paginator;
				this.dataSource.sort = this.sort;
				this.dataSource.filterPredicate = this.filterPredicate;
				
				// définition des règles pour le tri au sein du datasource
				this.dataSource.sortingDataAccessor = (item, property) => {
					switch (property) {
						case 'entreprises':
							return item.nomEntreprises;
						case 'parcs':
							return item.parcDescription;
						case 'ouvrages':
							return item.nomOuvrages;
						case 'typePdp': {
							return this.typePdpLabel(item);
						}
						case 'etat': {
							return this.etatPdpLabel(item);
						}
						case 'dateDebut': {
							const localDateDebut = [item.debutValidite[0], item.debutValidite[1], item.debutValidite[2]] as LocalDate;
							return this.conversionService.fromLocalDate(localDateDebut);
						}
						case 'dateFin': 
						case 'validite': 
						case 'joursAvantExpiration': {
							const localDateFin = [item.finValidite[0], item.finValidite[1], item.finValidite[2]] as LocalDate;
							return this.conversionService.fromLocalDate(localDateFin);
						}
						default: {
							// NB: etrangement, juste faire "item[property]" cause une erreur de compilation
							return (item as { [key: string]: any })[property];
						}
					}
				};
				
				// charger les pdps initialement
				this.loadPdps();
				this.searchCriteria.typePdp = [0, 1];
				this.searchCriteria.etat = [0, 1, 2, 3, 4, 6, 7];
				this.searchCriteria.validite = [0, 1];
			}
		);
	}
	
	loadPdps() {
		this.planPreventionService.loadPdps().pipe(
			catchError(
				(response: HttpErrorResponse) => {
					// Retrait de l'indicateur de chargement
					this.isLoading = false;
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors du chargement des plan de prévention (Erreur: " + response.status + ")");
					}
					throw new Error();
			})
		).subscribe(
			(results: PlanPreventionEntity[]) => {
				this.data = results;
				this.dataSource.data = this.data;
				// Retrait de l'indicateur de chargement
				this.isLoading = false;
			}
		);
	}
	
	typePdpLabel(pdp: PlanPreventionEntity): string {
		let typePdp = '';
		switch(pdp.typePdp) {
			case 0:
				typePdp = 'Général';
				break;
			case 1: 
				typePdp = 'Spécifique';
				break;
		}
 		return typePdp;
	}
	
	etatPdpLabel(pdp: PlanPreventionEntity): string {
	    if (pdp.obsolete) {
			return 'Archivé';
		} else if (pdp.etat <= applicationConstants.etatPdp.length) {
			return applicationConstants.etatPdp[pdp.etat];
		}
 		return '';
	}
	
	ajouterPdp() {
		console.log('demande ajout de pdp');
		let pdp: PlanPreventionEntity = new PlanPreventionEntity();
		pdp.mode = 'create';
		pdp.typePdp = 0; // par défaut le type pdp est général
		pdp.lastCompletedStep = 0; // par défaut aucun step n'est rempli
		this.pdpForEdit.emit(pdp);
	}
	
	export() {
		// construire les criteres pour export
		let exportCriteria = new PdpExportCriteria();
		if (this.searchCriteria.numeroPdp) {
			exportCriteria.numeroPdp = this.searchCriteria.numeroPdp;
		}
		if (this.searchCriteria.agence) {
			exportCriteria.agenceId = this.searchCriteria.agence;
		}
		if (this.searchCriteria.antenne) {
			exportCriteria.antenneId = this.searchCriteria.antenne;
		}
		if (this.searchCriteria.parc) {
			exportCriteria.parcId = this.searchCriteria.parc;
		}
		if (this.searchCriteria.entreprise) {
			exportCriteria.entrepriseId = this.searchCriteria.entreprise;
		}
		if (this.searchCriteria.typePdp.length > 0) {
			if (this.searchCriteria.typePdp.length === 2) {
				exportCriteria.generalSpe = [];
				exportCriteria.generalSpe.push(new PdpExportCriteriaOption('TOUS', true));
			} else {
				exportCriteria.generalSpe = [];
				exportCriteria.generalSpe.push(new PdpExportCriteriaOption(this.searchCriteria.typePdp[0] === 0 ? 'GENERAL' : 'SPECIFIQUE', true));
			}
		}
		if (this.searchCriteria.etat.length > 0) {
			if (this.searchCriteria.etat.length === 9) {
				exportCriteria.etat = [];
				exportCriteria.etat.push(new PdpExportCriteriaOption('TOUS', true));
			} else {
				exportCriteria.etat = [];
				for (let i in this.searchCriteria.etat) {
					switch (this.searchCriteria.etat[i]) {
						case 0:
							exportCriteria.etat.push(new PdpExportCriteriaOption('BROUILLON', true));
							break;
						case 1:
							exportCriteria.etat.push(new PdpExportCriteriaOption('A_FAIRE_SIGNER', true));
							break;
						case 2:
							exportCriteria.etat.push(new PdpExportCriteriaOption('ENVOYE_VIA_DOCUSIGN', true));
							break;
						case 3:
							exportCriteria.etat.push(new PdpExportCriteriaOption('SIGNE_PARTIELLEMENT', true));
							break;
						case 4:
							exportCriteria.etat.push(new PdpExportCriteriaOption('SIGNE', true));
							break;
						case 5:
							exportCriteria.etat.push(new PdpExportCriteriaOption('ARCHIVER', true));
							break;
						case 6:
							exportCriteria.etat.push(new PdpExportCriteriaOption('ATTENTE_DE_RELECTURE', true));
							break;
						case 7:
							exportCriteria.etat.push(new PdpExportCriteriaOption('INVALIDE', true));
							break;
					}
				}
			}
		}
		if (this.searchCriteria.validite.length > 0) {
			if (this.searchCriteria.validite.length === 1) {
				exportCriteria.validite = this.searchCriteria.validite[0] === 0 ? 'EN_COURS' : 'EXPIRE';
			}
		}
		console.log('exportCriteria: ', exportCriteria);
		this.planPreventionService.export(exportCriteria).subscribe(
			(blob: Blob) => {
				const objectUrl: string = URL.createObjectURL(blob);
				const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
				a.href = objectUrl;
				a.download = 'export-pdp.xls';
				document.body.appendChild(a);
				a.click();
				
				document.body.removeChild(a);
				URL.revokeObjectURL(objectUrl);
  			},
			(err: any) => {
				alert("Erreur lors du téléchargement de l'export");
    			console.log('Could not generate the file, error: ', err);
			}
		);
	}
	
	onPdpChanged(pdpId: number) {
		console.log('on a modifié le PDP avec id: ', pdpId);
		// rechargement des pdps
		this.loadPdps();
	}
	
	showPdpResume(pdp: PlanPreventionEntity): boolean {
		this.resumeDialogCommands.next(pdp);
		return false;
	}
	
	openSignatairesDialog(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			// si le parc est actif on autorise la signature
			switch(pdp.typePdp) {
				case 0:
					if (pdp.versionTechnique >= applicationConstants.versionInWhichValideursCanSignPdps) {
						// en v3++ on distingue la façon de signer un pdp général d'un spécifique
						this.dialogSignatairesPdpGeneralCommands.next(pdp);
					} else {
						// en v1 et v2 la signature des pdp generaux est identique à la signature des pdps spécifique
						this.dialogSignatairesPdpSpecificCommands.next(pdp);
					}
					break;
				case 1: 
					// affichage du popup de signature pour un pdp spécifique
					this.dialogSignatairesPdpSpecificCommands.next(pdp);
					break;
				default:
					// rien à faire ici
			}
			
		}
		return false;
	}
	
	openDialogForIntervenants(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			this.manageIntervenantsCommands.next(pdp.id);
		}
		return false;
	}

	openDialogForPdpLink(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			this.manageLinkCommands.next(pdp);
		}
		return false;
	}

	openDialogForGeneration(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			this.generationCommands.next(pdp);
		}
		return false;
	}
	
	openDialogForPdpRefreshValidation(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			this.refreshValidationCommands.next(pdp);
		}
		return false;
	}

	edit(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			pdp.mode = 'update';
			this.pdpForEdit.emit(pdp);
		}
		return false;
	}

	duplicate(pdp: PlanPreventionEntity): boolean {
		if (pdp.parcActif) {
			pdp.mode = 'duplicate';
			pdp.etat = EtatPdp.BROUILLON;
			this.pdpForEdit.emit(pdp);
		}
		return false;
	}
	
	archiver(pdp:PlanPreventionEntity): boolean {
		if (!pdp.obsolete) {
			if ( !confirm('Confirmez-vous l\'archivage du PDP N°' + pdp.numeroPdp + ' pour le parc ' + pdp.parcDescription + ' ?') ) {
				return false;
			}
		} else {
			if ( !confirm('Voulez-vous désarchiver le pdp ?') ) {
				return false;
			}
		}
		this.planPreventionService.archive(pdp.id).subscribe(
			() => {
				// rechargement des pdps
				this.loadPdps();
			}
		);
		return false;
	}

	supprimer(pdp:PlanPreventionEntity): boolean {
		if ( !confirm('Confirmez-vous la suppression du PDP N°' + pdp.numeroPdp + ' pour le parc ' + pdp.parcDescription + ' ?') ) {
			return false;
		}
		this.planPreventionService.supprimer(pdp.id).subscribe(
			() => {
				// rechargement des pdps
				this.loadPdps();
			}
		);
		return false;
	}

}